import React, { useState, useEffect } from 'react';
import { useApiClient } from '../services/api';
import { Hackday } from '../types/context';

interface SendMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sponsorId: string;
}

const SendMessageModal: React.FC<SendMessageModalProps> = ({ isOpen, onClose, sponsorId }) => {
  const apiClient = useApiClient();
  const [events, setEvents] = useState<Hackday[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [participantCount, setParticipantCount] = useState(0);

  const TITLE_MAX_LENGTH = 100;
  const MESSAGE_MAX_LENGTH = 500;

  // Fetch sponsor's events when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSponsorEvents();
      // Reset form when modal opens
      setSelectedEventId('');
      setTitle('');
      setMessage('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  // Update participant count when event is selected
  useEffect(() => {
    if (selectedEventId) {
      const selectedEvent = events.find(e => e.id === selectedEventId);
      setParticipantCount(selectedEvent?.participants?.length || 0);
    } else {
      setParticipantCount(0);
    }
  }, [selectedEventId, events]);

  const fetchSponsorEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await apiClient.getSponsorHackdays();
      if (response.success && response.data) {
        setEvents(response.data);
      } else {
        setError('Failed to load your events');
      }
    } catch (err) {
      console.error('Error fetching sponsor events:', err);
      setError('Failed to load your events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedEventId) {
      setError('Please select an event');
      return;
    }
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    if (title.length > TITLE_MAX_LENGTH) {
      setError(`Title must be ${TITLE_MAX_LENGTH} characters or less`);
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      setError(`Message must be ${MESSAGE_MAX_LENGTH} characters or less`);
      return;
    }
    if (participantCount === 0) {
      setError('This event has no participants to message');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.sendBroadcastMessage(
        selectedEventId,
        title.trim(),
        message.trim()
      );

      if (response.success && response.data) {
        setSuccess(`Message sent successfully to ${response.data.count} participant${response.data.count !== 1 ? 's' : ''}!`);
        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          setTitle('');
          setMessage('');
          setSelectedEventId('');
          onClose();
        }, 2000);
      } else {
        setError(response.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending broadcast message:', err);
      setError('An error occurred while sending the message');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-50 to-pink-50">
          <h2 className="text-2xl font-bold text-gray-800 font-coolvetica">Send Message to Participants</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Event Selector */}
          <div>
            <label htmlFor="event" className="block text-sm font-medium text-gray-700 mb-2">
              Select Event <span className="text-red-500">*</span>
            </label>
            {loadingEvents ? (
              <div className="text-sm text-gray-500">Loading your events...</div>
            ) : events.length === 0 ? (
              <div className="text-sm text-gray-500">No events found. Create an event first.</div>
            ) : (
              <select
                id="event"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                required
              >
                <option value="">Choose an event...</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title} ({event.participants?.length || 0} participants)
                  </option>
                ))}
              </select>
            )}
            {selectedEventId && participantCount > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                📢 This message will be sent to <strong>{participantCount}</strong> participant{participantCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Title Input */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Message Title <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={loading}
              maxLength={TITLE_MAX_LENGTH}
              placeholder="e.g., Important Update, New Bounty Available, etc."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
            <div className="mt-1 flex justify-between items-center">
              <p className="text-xs text-gray-500">Keep it concise and informative</p>
              <p className={`text-xs ${title.length > TITLE_MAX_LENGTH * 0.9 ? 'text-red-500' : 'text-gray-500'}`}>
                {title.length}/{TITLE_MAX_LENGTH}
              </p>
            </div>
          </div>

          {/* Message Textarea */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              maxLength={MESSAGE_MAX_LENGTH}
              rows={6}
              placeholder="Enter your message here..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              required
            />
            <div className="mt-1 flex justify-between items-center">
              <p className="text-xs text-gray-500">Be clear and actionable</p>
              <p className={`text-xs ${message.length > MESSAGE_MAX_LENGTH * 0.9 ? 'text-red-500' : 'text-gray-500'}`}>
                {message.length}/{MESSAGE_MAX_LENGTH}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingEvents || events.length === 0 || participantCount === 0}
              className="px-6 py-2 bg-[#782c8c] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendMessageModal;
